import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  useRequisitions,
  useCreateRequisition,
  useUpdateRequisition,
  useDeleteRequisition,
  useSubmitRequisitionForApproval,
  useApproveRequisition,
  useRejectRequisition,
  useRequestRevisionRequisition,
  useDelegateApproval,
} from '@/hooks/useRequisitions';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useCreatePurchaseOrder } from '@/hooks/usePurchaseOrders';
import { useVendors } from '@/hooks/useVendors';
import { useToast } from '@/hooks/useToast';
import type { Requisition, Location, Vendor } from '@/lib/schemas';
import { cn, formatCurrency } from '@/lib/utils';
import {
  Plus,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  AlertTriangle,
  Calendar,
  User,
  Send,
  MessageSquare,
  Share2,
  X,
  Package,
  Truck,
} from 'lucide-react';
import { ItemSelector } from '@/components/inventory/ItemSelector';
import { useBudget } from '@/hooks/useBudget';

const statusConfig = {
  draft: { variant: 'outline' as const, label: 'Draft' },
  submitted: { variant: 'warning' as const, label: 'Submitted' },
  pending_revision: { variant: 'info' as const, label: 'Pending Revision' },
  approved: { variant: 'success' as const, label: 'Approved' },
  rejected: { variant: 'error' as const, label: 'Rejected' },
  cancelled: { variant: 'outline' as const, label: 'Cancelled' },
  fulfilled: { variant: 'success' as const, label: 'Fulfilled' },
};

export default function RequisitionsPage() {
  const { user } = useAuth();
  const hospitalId = user?.hospital_id || '550e8400-e29b-41d4-a716-446655440000';

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingRequisition, setEditingRequisition] = useState<Requisition | null>(null);
  const [deletingRequisition, setDeletingRequisition] = useState<Requisition | null>(null);
  const [selectedRequisition, setSelectedRequisition] = useState<Requisition | null>(null);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | 'revision' | 'delegate' | null>(null);

  // Item selector state
  const [isItemSelectorOpen, setIsItemSelectorOpen] = useState(false);
  const [requisitionItems, setRequisitionItems] = useState<any[]>([]);

  // Conversion state
  const [convertingRequisition, setConvertingRequisition] = useState<Requisition | null>(null);
  const [isConversionModalOpen, setIsConversionModalOpen] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [orderDate, setOrderDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [poNotes, setPoNotes] = useState<string>('');

  const { data: vendors = { data: [], totalCount: 0 } } = useVendors();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { data: requisitionsResponse = { data: [], totalCount: 0 }, isLoading, error } = useRequisitions({
    page,
    limit: pageSize,
  });

  const requisitions = requisitionsResponse.data;
  const totalCount = requisitionsResponse.totalCount;

  useEffect(() => {
    setPage(1);
  }, [statusFilter, pageSize]);
  const createMutation = useCreateRequisition();
  const updateMutation = useUpdateRequisition();
  const deleteMutation = useDeleteRequisition();
  const submitApprovalMutation = useSubmitRequisitionForApproval();
  const approveMutation = useApproveRequisition();
  const rejectMutation = useRejectRequisition();
  const revisionMutation = useRequestRevisionRequisition();
  const delegateMutation = useDelegateApproval();
  const createPOMutation = useCreatePurchaseOrder();
  const toast = useToast();
  const { validateBudget } = useBudget();

  // Calculate total amount for budget validation
  const getRequisitionTotal = (req: Requisition) => {
    return req.items.reduce(
      (sum, item) => sum + (item.quantity_requested * (item.unit_cost || 0)),
      0
    );
  };

  const statusOptions = Object.entries(statusConfig).map(([value, { label }]) => ({
    value,
    label,
  }));

  const filteredRequisitions = useMemo(() => {
    return requisitions.filter((req) => {
      const matchesSearch =
        (req.requisition_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.requested_by.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || req.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [requisitions, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    requisitions.forEach((req) => {
      counts.all++;
      counts[req.status] = (counts[req.status] || 0) + 1;
    });
    return counts;
  }, [requisitions]);

  const getLocationName = (locationId: string) => {
    return `Location ${locationId}`;
  };

  // Get location options for the form
  const { data: locations = [] } = useLocations();
  const locationOptions = locations
    .filter((loc): loc is Location => !!loc.location_id)
    .map((loc) => ({
      value: loc.location_id!,
      label: `${loc.code} - ${loc.name}`,
    }));

  // Item management functions
  const handleAddItem = (item: any) => {
    setRequisitionItems(prev => [...prev, item]);
  };

  const handleRemoveItem = (itemId: string) => {
    setRequisitionItems(prev => prev.filter(i => i.item_id !== itemId));
  };

  // Approve actions
  const openApprovalAction = (requisition: Requisition, action: 'approve' | 'reject' | 'revision' | 'delegate') => {
    setSelectedRequisition(requisition);
    setApprovalAction(action);
    setIsApprovalModalOpen(true);
  };

  const submitApprovalAction = async (data: { comments?: string; delegateToId?: string }) => {
    if (!selectedRequisition?.requisition_id || !approvalAction) return;

    const approverId = 'current-user';

    try {
      switch (approvalAction) {
        case 'approve':
          // Validate budget before approval
          const totalAmount = getRequisitionTotal(selectedRequisition);
          if (totalAmount > 0) {
            const budgetCheck = await validateBudget(totalAmount, selectedRequisition.department_id).refetch();
            if (budgetCheck.data && !budgetCheck.data.valid) {
              toast.error('Budget exceeded', budgetCheck.data.message || 'Insufficient budget for this requisition.');
              return;
            }
          }
          await approveMutation.mutateAsync({
            id: selectedRequisition.requisition_id,
            approverId,
            comments: data.comments || 'Approved',
          });
          toast.success('Requisition approved', 'Requisition has been approved successfully.');
          break;
        case 'reject':
          await rejectMutation.mutateAsync({
            id: selectedRequisition.requisition_id,
            approverId,
            comments: data.comments || 'Rejected',
          });
          toast.success('Requisition rejected', 'Requisition has been rejected.');
          break;
        case 'revision':
          await revisionMutation.mutateAsync({
            id: selectedRequisition.requisition_id,
            approverId,
            comments: data.comments || 'Revision requested',
          });
          toast.success('Revision requested', 'Revision has been requested for this requisition.');
          break;
        case 'delegate':
          if (data.delegateToId) {
            await delegateMutation.mutateAsync({
              id: selectedRequisition.requisition_id,
              approverId,
              delegateToId: data.delegateToId,
            });
            toast.success('Approval delegated', 'Approval has been delegated successfully.');
          }
          break;
      }
      setIsApprovalModalOpen(false);
      setSelectedRequisition(null);
      setApprovalAction(null);
    } catch (error: any) {
      toast.error('Action failed', error.message || 'An unknown error occurred');
    }
  };

  // Convert requisition to PO
  const openConvertModal = (requisition: Requisition) => {
    setConvertingRequisition(requisition);
    setSelectedVendorId('');
    setOrderDate(new Date().toISOString().split('T')[0]);
    setDeliveryDate('');
    setPoNotes(`Converted from requisition ${requisition.requisition_number}`);
    setIsConversionModalOpen(true);
  };

  const handleConvertToPO = async () => {
    if (!convertingRequisition || !selectedVendorId || !convertingRequisition.requisition_id) return;

    try {
      await createPOMutation.mutateAsync({
        hospital_id: convertingRequisition.hospital_id,
        vendor_id: selectedVendorId,
        order_date: orderDate,
        delivery_date: deliveryDate || undefined,
        notes: poNotes,
        items: convertingRequisition.items.map(item => ({
          item_id: item.item_id,
          quantity_ordered: item.quantity_requested,
          unit_price: item.unit_cost || 0,
          line_total: (item.quantity_requested * (item.unit_cost || 0)),
          available_quantity: 0,
        })),
        status: 'draft',
        traceability_level: 'none',
        total_amount: convertingRequisition.items.reduce((sum, item) =>
          sum + (item.quantity_requested * (item.unit_cost || 0)), 0),
      });

      // Update requisition status to "fulfilled"
      await updateMutation.mutateAsync({
        id: convertingRequisition.requisition_id!,
        data: { status: 'fulfilled' as const },
      });

      setIsConversionModalOpen(false);
      setConvertingRequisition(null);
      setSelectedVendorId('');
      setPoNotes('');
      toast.success('Purchase order created', 'Requisition converted to purchase order successfully.');
    } catch (error: any) {
      toast.error('Failed to convert requisition', error.message || 'An unknown error occurred');
    }
  };

  // CRUD operations
  const handleCreate = async (data: Partial<Requisition>) => {
    try {
      await createMutation.mutateAsync({
        ...data,
        items: requisitionItems.map(item => ({
          item_id: item.item_id,
          quantity_requested: item.quantity_ordered,
          unit_cost: item.unit_price,
        })),
      } as Omit<Requisition, 'requisition_id' | 'requisition_number' | 'requested_at' | 'created_at' | 'updated_at'>);
      setIsCreateOpen(false);
      setRequisitionItems([]);
      toast.success('Requisition created', 'Requisition has been created successfully.');
    } catch (error: any) {
      toast.error('Failed to create requisition', error.message || 'An unknown error occurred');
    }
  };

  const handleUpdate = async (data: Partial<Requisition>) => {
    if (editingRequisition && editingRequisition.requisition_id) {
      try {
        await updateMutation.mutateAsync({
          id: editingRequisition.requisition_id,
          data: {
            ...data,
            items: requisitionItems.map(item => ({
              item_id: item.item_id,
              quantity_requested: item.quantity_ordered,
              unit_cost: item.unit_price,
            })),
          } as Partial<Omit<Requisition, 'requisition_id' | 'requisition_number' | 'requested_at' | 'created_at'>>,
        });
        setEditingRequisition(null);
        setRequisitionItems([]);
        toast.success('Requisition updated', 'Requisition has been updated successfully.');
      } catch (error: any) {
        toast.error('Failed to update requisition', error.message || 'An unknown error occurred');
      }
    }
  };

  const handleDelete = async () => {
    if (deletingRequisition && deletingRequisition.requisition_id) {
      try {
        await deleteMutation.mutateAsync(deletingRequisition.requisition_id);
        setDeletingRequisition(null);
        toast.success('Requisition deleted', 'Requisition has been deleted successfully.');
      } catch (error: any) {
        toast.error('Failed to delete requisition', error.message || 'An unknown error occurred');
      }
    }
  };

  const handleSubmitForApproval = async (requisition: Requisition) => {
    if (requisition.requisition_id) {
      try {
        await submitApprovalMutation.mutateAsync(requisition.requisition_id);
        toast.success('Requisition submitted', 'Requisition has been submitted for approval.');
      } catch (error: any) {
        toast.error('Failed to submit requisition', error.message || 'An unknown error occurred');
      }
    }
  };

  // Action buttons logic
  const getActionButtons = (requisition: Requisition) => {
    const buttons: JSX.Element[] = [];

    // Draft actions
    if (requisition.status === 'draft') {
      buttons.push(
        <Button
          key="submit"
          size="sm"
          variant="primary"
          onClick={() => handleSubmitForApproval(requisition)}
          disabled={submitApprovalMutation.isPending}
          className="gap-1"
        >
          <Send className="h-4 w-4" />
          Submit
        </Button>
      );
    }

    // Submitted - approval actions
    if (requisition.status === 'submitted' && requisition.approval_chain) {
      buttons.push(
        <Button
          key="approve"
          size="sm"
          variant="primary"
          onClick={() => openApprovalAction(requisition, 'approve')}
          className="gap-1"
        >
          <CheckCircle className="h-4 w-4" />
          Approve
        </Button>,
        <Button
          key="reject"
          size="sm"
          variant="destructive"
          onClick={() => openApprovalAction(requisition, 'reject')}
          className="gap-1"
        >
          <XCircle className="h-4 w-4" />
          Reject
        </Button>,
        <Button
          key="revision"
          size="sm"
          variant="outline"
          onClick={() => openApprovalAction(requisition, 'revision')}
          className="gap-1"
        >
          <MessageSquare className="h-4 w-4" />
          Request Revision
        </Button>,
        <Button
          key="delegate"
          size="sm"
          variant="ghost"
          onClick={() => openApprovalAction(requisition, 'delegate')}
          className="gap-1"
        >
          <Share2 className="h-4 w-4" />
          Delegate
        </Button>
      );
    }

    // Approved - show convert to PO button
    if (requisition.status === 'approved') {
      buttons.push(
        <Button
          key="convert"
          size="sm"
          variant="secondary"
          onClick={() => openConvertModal(requisition)}
          className="gap-1"
        >
          <Truck className="h-4 w-4" />
          Convert to PO
        </Button>
      );
    }

    return buttons;
  };

  // Form fields
  const requisitionFields = [
    {
      name: 'hospital_id',
      label: 'Hospital ID',
      type: 'number' as const,
      required: true,
      placeholder: 'Enter hospital ID',
    },
    {
      name: 'location_id',
      label: 'Location',
      type: 'select' as const,
      required: true,
      options: locationOptions,
      helperText: 'Select the location for this requisition',
    },
    {
      name: 'requested_by',
      label: 'Requested By',
      type: 'text' as const,
      required: true,
      placeholder: 'Enter requester name',
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea' as const,
      placeholder: 'Additional notes (optional)',
    },
  ] as const satisfies FormField[];

  const approvalFields: FormField[] = [
    {
      name: 'comments',
      label: 'Comments',
      type: 'textarea',
      placeholder: 'Add comments (required for reject/revision)',
      helperText: 'Comments help the requestor understand your decision',
    },
  ];

  return (
    <div className="space-y-6 overflow-hidden">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Requisitions
          </h1>
          <p className="text-text-secondary mt-1">
            Manage purchase requests and approvals
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Requisition
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading requisitions</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-3">
        {[
          { label: 'All', value: stats.all, status: 'all' as const, color: 'bg-blue-100 text-blue-700' },
          { label: 'Draft', value: stats.draft || 0, status: 'draft' as const, color: 'bg-gray-100 text-gray-700' },
          { label: 'Submitted', value: stats.submitted || 0, status: 'submitted' as const, color: 'bg-yellow-100 text-yellow-700' },
          { label: 'Revision', value: stats.pending_revision || 0, status: 'pending_revision' as const, color: 'bg-purple-100 text-purple-700' },
          { label: 'Approved', value: stats.approved || 0, status: 'approved' as const, color: 'bg-green-100 text-green-700' },
          { label: 'Rejected', value: stats.rejected || 0, status: 'rejected' as const, color: 'bg-red-100 text-red-700' },
          { label: 'Cancelled', value: stats.cancelled || 0, status: 'cancelled' as const, color: 'bg-gray-100 text-gray-700' },
        ].map(({ label, value, status: statStatus }) => (
          <Card
            key={label}
            className={cn(
              'cursor-pointer transition-all min-w-0 truncate',
              statusFilter === statStatus ? 'ring-2 ring-primary' : ''
            )}
            onClick={() => setStatusFilter(statStatus === 'all' ? 'all' : statusFilter === statStatus ? 'all' : statStatus)}
          >
            <CardContent className="pt-3 pb-2 px-2">
              <div className="flex items-center gap-2">
                <div className={cn('p-1.5 rounded-lg shrink-0', statStatus === 'all' ? 'bg-blue-100' :
                  statStatus === 'approved' ? 'bg-green-100' :
                  statStatus === 'rejected' ? 'bg-red-100' :
                  statStatus === 'submitted' ? 'bg-yellow-100' : 'bg-gray-100')}>
                  <FileText className={cn('h-4 w-4',
                    statStatus === 'all' ? 'text-blue-600' :
                    statStatus === 'approved' ? 'text-green-600' :
                    statStatus === 'rejected' ? 'text-red-600' :
                    statStatus === 'submitted' ? 'text-yellow-600' : 'text-gray-600')} />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-text-primary truncate">{value}</p>
                  <p className="text-xs text-text-muted truncate">{label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
                aria-label="Search requisitions"
              />
            </div>
            <div className="flex gap-2 shrink-0">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={10} columns={7} />
      ) : filteredRequisitions.length > 0 ? (
        <div className="overflow-x-auto -mx-4 sm:-mx-6">
        <DataTable
          columns={[
            {
              key: 'requisition_number',
              header: 'Requisition #',
              render: (req: Requisition) => (
                <div>
                  <p className="font-medium text-text-primary">{req.requisition_number}</p>
                  <p className="text-xs text-text-muted flex items-center gap-1 mt-1">
                    <Calendar className="h-3 w-3" />
                    {req.requested_at ? new Date(req.requested_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              ),
            },
            {
              key: 'requested_by',
              header: 'Requested By',
              render: (req: Requisition) => (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-text-muted" />
                  <span className="text-text-secondary">{req.requested_by}</span>
                </div>
              ),
            },
            {
              key: 'location_id',
              header: 'Location',
              render: (req: Requisition) => (
                <span className="text-text-secondary">{getLocationName(req.location_id)}</span>
              ),
            },
            {
              key: 'items',
              header: 'Items',
              render: (req: Requisition) => (
                <div className="text-sm">
                  <p className="font-medium">{req.items.length} item(s)</p>
                  <p className="text-text-muted text-xs">
                    {req.items.reduce((sum, item) => sum + item.quantity_requested, 0)} total quantity
                  </p>
                </div>
              ),
            },
            {
              key: 'total',
              header: 'Total',
              render: (req: Requisition) => {
                const total = req.items.reduce(
                  (sum, item) => sum + (item.quantity_requested * (item.unit_cost || 0)),
                  0
                );
                return <span className="font-mono text-text-primary">{formatCurrency(total)}</span>;
              },
            },
            {
              key: 'status',
              header: 'Status',
              render: (req: Requisition) => {
                const config = statusConfig[req.status as keyof typeof statusConfig];
                const isUrgent = req.status === 'submitted' && req.required_date && 
                  new Date(req.required_date) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000); // 3 days
                
                return (
                  <div className="flex flex-col gap-1">
                    <Badge variant={config.variant}>
                      {req.status === 'submitted' && <Clock className="h-3 w-3 mr-1" />}
                      {req.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                      {req.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                      {req.status === 'pending_revision' && <MessageSquare className="h-3 w-3 mr-1" />}
                      {config.label}
                    </Badge>
                    {isUrgent && (
                      <div className="flex items-center gap-1 text-xs text-red-600">
                        <AlertTriangle className="h-3 w-3" />
                        <span>Urgent</span>
                      </div>
                    )}
                    {req.required_date && req.status === 'submitted' && (
                      <div className="flex items-center gap-1 text-xs text-text-muted">
                        <Calendar className="h-3 w-3" />
                        <span>Required: {new Date(req.required_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                );
              },
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (req: Requisition) => (
                <div className="flex items-center gap-2">
                  {getActionButtons(req)}
                </div>
              ),
            },
          ]}
          data={filteredRequisitions}
          loading={false}
          onEdit={setEditingRequisition}
          onDelete={setDeletingRequisition}
          getItemId={(req) => req.requisition_id!}
          actionsColumn={true}
        />
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No requisitions found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first requisition to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <ModalForm<Requisition>
        open={isCreateOpen || !!editingRequisition}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setEditingRequisition(null);
            setRequisitionItems([]);
          }
        }}
        title={editingRequisition ? 'Edit Requisition' : 'New Requisition'}
        description="Create a requisition for items needed."
        fields={requisitionFields}
        initialData={editingRequisition || undefined}
        onSubmit={editingRequisition ? handleUpdate : handleCreate}
        isLoading={createMutation.isPending || updateMutation.isPending}
      >
        <div className="space-y-6">
          {/* Items Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Requisition Items</h3>
              <Button
                type="button"
                size="sm"
                onClick={() => setIsItemSelectorOpen(true)}
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </Button>
            </div>

            {requisitionItems.length > 0 ? (
              <div className="space-y-2">
                {requisitionItems.map((item) => (
                  <div
                    key={item.item_id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-background"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-text-primary">{item.item_name}</p>
                      <p className="text-sm text-text-muted">
                        {item.item_code} • {item.unit_of_measure}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-text-muted">Quantity</p>
                        <p className="font-semibold">{item.quantity_ordered}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-text-muted">Unit Price</p>
                        <p className="font-mono">{formatCurrency(item.unit_price)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-text-muted">Total</p>
                        <p className="font-mono font-semibold">{formatCurrency(item.line_total)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(item.item_id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-end items-center gap-2">
                    <span className="text-sm text-text-muted">Total:</span>
                    <span className="text-xl font-bold font-mono">
                      {formatCurrency(requisitionItems.reduce((sum, item) => sum + item.line_total, 0))}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center">
                  <Package className="h-12 w-12 text-text-muted mx-auto mb-3 opacity-50" />
                  <p className="text-text-secondary">No items added yet</p>
                  <p className="text-sm text-text-muted mt-1">
                    Click "Add Item" to select items from the catalog
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </ModalForm>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deletingRequisition}
        onOpenChange={(open) => !open && setDeletingRequisition(null)}
        title="Delete Requisition"
        description={`Are you sure you want to delete requisition "${deletingRequisition?.requisition_number}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
      />

      {/* Approval Action Modal */}
      <ModalForm
        open={isApprovalModalOpen}
        onOpenChange={setIsApprovalModalOpen}
        title={
          approvalAction === 'approve' ? 'Approve Requisition' :
          approvalAction === 'reject' ? 'Reject Requisition' :
          approvalAction === 'revision' ? 'Request Revision' :
          'Delegate Approval'
        }
        description={`You are about to ${approvalAction} requisition ${selectedRequisition?.requisition_number}`}
        fields={approvalFields}
        onSubmit={submitApprovalAction}
        isLoading={approveMutation.isPending || rejectMutation.isPending || revisionMutation.isPending || delegateMutation.isPending}
      />

      {/* Item Selector Modal */}
      <ItemSelector
        open={isItemSelectorOpen}
        onOpenChange={setIsItemSelectorOpen}
        onSelect={handleAddItem}
        hospitalId={hospitalId}
      />

      {/* Convert to PO Modal */}
      <ModalForm
        open={isConversionModalOpen}
        onOpenChange={setIsConversionModalOpen}
        title="Convert Requisition to Purchase Order"
        description={`Create a purchase order from requisition ${convertingRequisition?.requisition_number}`}
        fields={[
          {
            name: 'vendor_id',
            label: 'Vendor',
            type: 'select' as const,
            required: true,
            options: Array.isArray(vendors.data)
              ? vendors.data
                  .filter((v) => !!v.vendor_id)
                  .map((v) => ({
                    value: v.vendor_id!,
                    label: `${v.code} - ${v.name}`,
                  }))
              : [],
            helperText: 'Select the vendor for this purchase order',
          },
          {
            name: 'order_date',
            label: 'Order Date',
            type: 'date' as const,
            required: true,
          },
          {
            name: 'delivery_date',
            label: 'Expected Delivery Date',
            type: 'date' as const,
          },
          {
            name: 'notes',
            label: 'Notes',
            type: 'textarea' as const,
          },
        ]}
        initialData={{
          hospital_id: convertingRequisition?.hospital_id,
          vendor_id: selectedVendorId,
          order_date: orderDate,
          delivery_date: deliveryDate,
          notes: poNotes,
        }}
        onSubmit={() => {
          handleConvertToPO();
        }}
        isLoading={createPOMutation.isPending}
      />
    </div>
  );
}
